"use client"

import type { Comment } from "@/types/const.types"
import { findElementFromPath } from "@/utils/find-element-from-path"
import {
	Avatar,
	AvatarFallback,
	AvatarImage,
} from "@preview-kit/ui/components/avatar"
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@preview-kit/ui/components/popover"
import { Separator } from "@preview-kit/ui/components/separator"
import { cn } from "@preview-kit/ui/lib/utils"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import { useCallback, useEffect, useState } from "react"
import "./index.css"

dayjs.extend(relativeTime)

const formatTimestamp = (timestamp: string | Date): string => {
	// Parse the timestamp - handle ISO strings, Date objects converted to strings, etc.
	const date = dayjs(timestamp)

	// Validate that we have a valid date
	if (!date.isValid()) {
		return "Invalid date"
	}

	const now = dayjs()
	const diffInMinutes = now.diff(date, "minute")
	const diffInHours = now.diff(date, "hour")
	const diffInDays = now.diff(date, "day")

	// Less than 1 minute ago
	if (diffInMinutes < 1) {
		return "just now"
	}

	// Less than 1 hour ago
	if (diffInHours < 1) {
		return `${diffInMinutes}m ago`
	}

	// Less than 24 hours ago
	if (diffInDays < 1) {
		return `${diffInHours}h ago`
	}

	// Less than 7 days ago
	if (diffInDays < 7) {
		return `${diffInDays}d ago`
	}

	// More than 7 days ago, show actual date
	if (diffInDays < 365) {
		return date.format("MMM D")
	}

	// More than 1 year ago, show year as well
	return date.format("MMM D, YYYY")
}

interface CommentMarkerProps {
	comment: Comment
	isSelected: boolean
	onSelectionChange: (comment: Comment | null) => void
}

export function CommentMarker({
	comment,
	isSelected,
	onSelectionChange,
}: CommentMarkerProps) {
	const [position, setPosition] = useState({ x: 0, y: 0 })
	const [showOnLeft, setShowOnLeft] = useState(false)

	const calculatePosition = useCallback(() => {
		const el = findElementFromPath(comment.targetPath)

		if (!el) return

		const rect = el.getBoundingClientRect()

		const absoluteX =
			rect.left + rect.width * comment.xPercentOffset + window.scrollX
		const absoluteY =
			rect.top + rect.height * comment.yPercentOffset + window.scrollY

		// Check if there's enough space on the right side for the expanded width (280px for w-70)
		const expandedWidth = 280
		const spaceOnRight =
			window.innerWidth - (rect.left + rect.width * comment.xPercentOffset)
		setShowOnLeft(spaceOnRight < expandedWidth)

		setPosition({ x: absoluteX, y: absoluteY })
	}, [comment])

	// recalculate position on mount/unmount and on window resize/scroll
	useEffect(() => {
		calculatePosition()

		window.addEventListener("resize", calculatePosition)
		window.addEventListener("scroll", calculatePosition)
		return () => {
			window.removeEventListener("resize", calculatePosition)
			window.removeEventListener("scroll", calculatePosition)
		}
	}, [calculatePosition])

	return (
		<div
			key={comment.id}
			className={`comment-marker absolute cursor-pointer ${
				!isSelected ? "group" : ""
			}`}
			style={{
				left: position.x,
				top: position.y,
			}}
			role="button"
			aria-label={`Comment by ${comment.user?.name}: ${comment.content.slice(0, 50)}${comment.content.length > 50 ? "..." : ""}`}
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault()
					onSelectionChange(comment)
				}
			}}
		>
			<Popover
				open={isSelected}
				onOpenChange={(open) => {
					if (!open) {
						onSelectionChange(null)
					}
				}}
			>
				<PopoverTrigger asChild>
					<div
						className="relative"
						onClick={(e) => {
							e.stopPropagation()
							onSelectionChange(isSelected ? null : comment)
						}}
					>
						<div
							className={cn(
								"w-8 h-8 overflow-hidden absolute transition-[width,padding,height] duration-150 p-1 bg-[#ee0b4b] flex items-center justify-center",
								"top-0",
								"transform -translate-y-full",
								"group-hover:z-10",
								"rounded-t-3xl",

								{
									// Non-selected states (hover effects)
									"group-hover:p-2 group-hover:h-16": !isSelected,
									"group-hover:w-70": !isSelected,
									// Position on left when not enough space on right
									"rounded-br-3xl":
										(!isSelected && !showOnLeft) || (isSelected && !showOnLeft),
									"right-0 rounded-bl-3xl": !isSelected && showOnLeft,
								},
							)}
						>
							<Avatar
								className={cn(
									"group-hover:mb-auto group-hover:mt-1",
									`w-7 h-7 mr-auto `,
								)}
							>
								<AvatarImage src={comment.user?.image} />
								<AvatarFallback>{comment.user?.name[0]}</AvatarFallback>
							</Avatar>

							{!isSelected && (
								<div
									className={cn(
										"opacity-0 w-full ml-2 hidden group-hover:block group-hover:opacity-100 transition-opacity duration-300 truncate text-white rounded-lg overflow-hidden",
										"self-start",
									)}
								>
									<p className="text-xs ">{comment.user?.name}</p>
									<p className="text-xs truncate">{comment.content}</p>
									<p className="text-xs mt-0.5 opacity-75">
										{comment.createdAt
											? formatTimestamp(comment.createdAt)
											: ""}
									</p>
								</div>
							)}
						</div>
					</div>
				</PopoverTrigger>
				<PopoverContent
					className={cn(
						"popover_comment text-black",
						"w-80 p-0 bg-white/90 shadow-xl",
						"border border-neutral-300",
					)}
					side={"right"}
					align="center"
					container={window.__PREVIEWKIT_PORTAL_CONTAINER__}
					sideOffset={45}
				>
					<div className="space-y-1">
						<div className="p-4">
							<div className="flex items-start gap-3">
								<Avatar className="w-8 h-8 flex-shrink-0">
									<AvatarImage src={comment.user?.image} />
									<AvatarFallback>{comment.user?.name[0]}</AvatarFallback>
								</Avatar>
								<div className="flex items-center gap-2 justify-center min-w-0">
									<p className="text-sm font-semibold">{comment.user?.name}</p>
									<p className="text-xs text-gray-400">
										{comment.createdAt
											? formatTimestamp(comment.createdAt)
											: ""}
									</p>
								</div>
							</div>
							<div className="pl-11">
								<p className="text-sm break-words whitespace-pre-wrap">
									{comment.content}
								</p>
							</div>
						</div>
						<Separator className="!h-[1px] bg-neutral-300" />
						<div className="p-4 pt-3">
							<div className="flex items-center gap-3">
								<div className="flex-1 relative">
									<input
										type="text"
										placeholder="Reply..."
										className="w-full px-3 py-2 text-sm bg-transparent border-0 rounded-lg focus:outline-none focus:ring-0 placeholder-gray-500"
									/>
								</div>
								<div className="flex items-center gap-2">
									<button className="w-6 h-6 flex items-center justify-center text-gray-500">
										<svg
											width="16"
											height="16"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
										>
											<rect
												x="3"
												y="3"
												width="18"
												height="18"
												rx="2"
												ry="2"
											></rect>
											<circle cx="9" cy="9" r="2"></circle>
											<path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
										</svg>
									</button>
									<button className="w-6 h-6 flex items-center justify-center text-gray-500 font-semibold text-sm">
										Aa
									</button>
									<button className="w-6 h-6 flex items-center justify-center text-gray-500">
										<svg
											width="16"
											height="16"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
										>
											<line x1="22" y1="2" x2="11" y2="13"></line>
											<polygon points="22,2 15,22 11,13 2,9 22,2"></polygon>
										</svg>
									</button>
								</div>
							</div>
						</div>
					</div>
				</PopoverContent>
			</Popover>
		</div>
	)
}
